import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { KAJABI, CONCERT_BG } from "@/lib/data";

const highlights = [
  "Weekly small-group cohort (5 months)",
  "Direct access to Devon & Becki DeVries",
  "Thousands of pitches per cohort",
  "60+ active briefs shared per cohort",
  "One live music supervisor visit each month",
  "Free access to C.A.M.P.",
  "Placements on Verizon, Walmart, NCIS, Dr. Odyssey, American Idol, The Equalizer, Ginny & Georgia and more",
  "Collaboration with other Mastermind members, plus creators outside the Mastermind",
];

export default function Mastermind() {
  return (
    <div data-testid="mastermind-page">
      {/* Hero */}
      <section
        data-testid="mm-hero"
        className="relative overflow-hidden grain border-b border-ink-600"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${CONCERT_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/85 via-ink-900/85 to-ink-900" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-36 md:pt-44 pb-24">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <p className="tag-kicker">Application · Small Cohort</p>
            <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.1] tracking-normal mt-5">
              The <span className="text-amber italic">Mastermind.</span>
            </h1>
            <p className="mt-8 text-xl md:text-2xl text-white/80 max-w-3xl leading-snug">
              A select few songwriters, artists and producers. Five months.
              Direct access to Devon &amp; Becki. Real representation. Real
              placements.
            </p>
            <p className="mt-4 font-mono text-xs text-white/50 uppercase tracking-widest">
              Acceptance by application and Zoom interview
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why + CTA */}
      <section
        data-testid="mm-body"
        className="relative bg-ink-900 py-24 md:py-32"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-14">
          {/* Left: Story */}
          <div className="lg:col-span-7">
            <p className="tag-kicker">Why the Mastermind</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-normal leading-[1.15] mt-3">
              Authentic tastemakers. Not sound-alikes.
            </h2>
            <p className="mt-6 text-white/70 text-lg leading-relaxed">
              Our Mastermind members become real voices supervisors trust. Not
              approximation writers. Not library content. Premium, pitched
              constantly, building catalogs that actually earn.
            </p>

            <ul className="mt-10 space-y-4">
              {highlights.map((h, i) => (
                <li
                  key={i}
                  data-testid={`mm-highlight-${i}`}
                  className="flex items-start gap-3"
                >
                  <span className="mt-1 text-amber">
                    <Check size={18} strokeWidth={2.5} />
                  </span>
                  <span className="text-white/80">{h}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 p-6 border border-amber/30 bg-amber/5">
              <p className="font-mono text-xs text-amber uppercase tracking-widest mb-2">
                Commitment
              </p>
              <p className="text-white">
                5-month cohort. Weekly live Zooms with Devon &amp; Becki.
                High-criteria acceptance. If selected, expect to work.
              </p>
            </div>
          </div>

          {/* Right: CTA card */}
          <div className="lg:col-span-5">
            <div
              data-testid="mm-cta-card"
              className="sticky top-28 relative overflow-hidden border border-amber/40 bg-gradient-to-br from-ink-800 to-ink-900 p-8 md:p-10 shadow-[0_20px_60px_-20px_rgba(34,197,94,0.2)]"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-amber/10 to-transparent pointer-events-none" />
              <div className="relative">
                <p className="tag-kicker">Ready?</p>
                <h3 className="font-display text-3xl md:text-4xl font-bold leading-[1.15] tracking-normal mt-4">
                  Start your{" "}
                  <span className="text-amber italic">application</span>.
                </h3>
                <p className="mt-4 text-white/70 leading-relaxed">
                  The full application takes about 10 minutes. You'll share
                  your music, where you are in your career, and why the
                  Mastermind is your next move. We review every submission
                  personally and respond within a week.
                </p>

                <a
                  href={KAJABI.mastermind}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mm-application-cta"
                  className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-amber hover:bg-amber-hover text-black font-bold uppercase tracking-widest text-sm px-6 py-5 rounded-sm transition-all group"
                >
                  Apply Now
                  <ArrowRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </a>

                <div className="mt-6 pt-6 border-t border-ink-600 grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="font-display text-2xl font-bold text-amber">
                      5 mo.
                    </p>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-white/50 mt-1">
                      Cohort length
                    </p>
                  </div>
                  <div>
                    <p className="font-display text-2xl font-bold text-amber">
                      Small
                    </p>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-white/50 mt-1">
                      Group size
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
